import { createContext, PropsWithChildren, useContext } from 'react';
import { ClientData } from './types';
import { useOpenAPIData } from '../../framework/openapi';
import { ClientSettingsProvider } from '@iptor/base';

export type Locale = string;

// default value used only when no provider is setup
export const ClientDataContext = createContext<ClientData>({
  currency: '',
  reportingCurrency: '',
  handler: '',
  applications: [],
  functionControls: [],
});

export const ClientDataContextProvider = ClientDataContext.Provider;

export type ClientDataProviderType = {
  localeSettings: {
    locale: Locale;
    dateFormat: string;
    decimalSeparator: string;
    groupSeparator: string;
  };
};

export const ClientDataProvider = ({ localeSettings, children }: PropsWithChildren<ClientDataProviderType>) => {
  const companyData = useOpenAPIData('internal.v1.base-general', 'GET /companies/current');
  const userData = useOpenAPIData('internal.v1.base-tables', 'GET /DC1Users/current');
  const applications = useOpenAPIData('internal.v1.base-tables', 'GET /applications');
  const functionControls = useOpenAPIData('internal.v1.base-tables', 'GET /functionControls');

  const value = {
    applications: applications.response?.data?.data?.items ?? [],
    functionControls: functionControls.response?.data?.data?.items ?? [],
    currency:
      (companyData?.response?.data?.data?.systemCurrencyISO || companyData?.response?.data?.data?.systemCurrency) ?? '',
    reportingCurrency:
      (companyData?.response?.data?.data?.reportingCurrencyISO ||
        companyData?.response?.data?.data?.reportingCurrency) ??
      '',
    handler: userData?.response?.data?.data?.handler ?? '',
  };

  const { currency, reportingCurrency } = value;

  return (
    <ClientDataContext.Provider value={value}>
      <ClientSettingsProvider
        value={{
          ...localeSettings,
          currency,
          reportingCurrency,
        }}
      >
        {children}
      </ClientSettingsProvider>
    </ClientDataContext.Provider>
  );
};

export const useClientData = () => {
  const { applications, handler, functionControls } = useContext(ClientDataContext);
  return {
    handler,
    applications,
    functionControls,
  };
};
